import { Card, CardContent } from "@/components/ui/card";
import { Home, Building2, Factory, ShoppingBag, LucideIcon } from "lucide-react";
import { AnimatedSection } from "./AnimatedSection";

interface ServiceArea {
  icon: LucideIcon;
  title: string;
  accentColor: string;
  gradientFrom: string;
  gradientTo: string;
  services: string[];
}

const serviceAreas: ServiceArea[] = [
  {
    icon: Home,
    title: "RESIDENCIAL",
    accentColor: "from-blue-400 to-blue-600",
    gradientFrom: "rgba(96, 165, 250, 0.3)",
    gradientTo: "rgba(59, 130, 246, 0.1)",
    services: [
      "Casas particulares",
      "Departamentos y lofts",
      "Condominios",
      "Bodegas y estacionamientos",
      "Edificios residenciales"
    ]
  },
  {
    icon: Building2,
    title: "INSTITUCIONAL",
    accentColor: "from-emerald-400 to-emerald-600",
    gradientFrom: "rgba(52, 211, 153, 0.3)",
    gradientTo: "rgba(16, 185, 129, 0.1)",
    services: [
      "Instituciones educacionales",
      "Centros médicos y clínicas",
      "Instituciones deportivas",
      "Edificios corporativos",
      "Centros gubernamentales"
    ]
  },
  {
    icon: Factory,
    title: "INDUSTRIAL",
    accentColor: "from-orange-400 to-orange-600",
    gradientFrom: "rgba(251, 146, 60, 0.3)",
    gradientTo: "rgba(234, 88, 12, 0.1)",
    services: [
      "Plantas de producción",
      "Centros de distribución",
      "Bodegas industriales",
      "Talleres y maestranzas",
      "Subestaciones eléctricas"
    ]
  },
  {
    icon: ShoppingBag,
    title: "COMERCIAL",
    accentColor: "from-violet-400 to-violet-600",
    gradientFrom: "rgba(167, 139, 250, 0.3)",
    gradientTo: "rgba(139, 92, 246, 0.1)",
    services: [
      "Tiendas y retail",
      "Hoteles y restaurantes",
      "Oficinas y coworking",
      "Constructoras",
      "Estudios de arquitectura"
    ]
  }
];

const ServiceAreaCard = ({ area, index }: { area: ServiceArea; index: number }) => {
  const Icon = area.icon;
  
  return (
    <AnimatedSection animation="slide-up" delay={index * 100}>
      <Card 
        className="h-full min-h-[320px] sm:min-h-[340px] flex flex-col backdrop-blur-xl bg-white/5 border-2 border-white/10 hover:border-white/30 group cursor-pointer relative overflow-hidden shadow-liquid-lg hover:shadow-liquid-xl transition-all duration-500"
      >
        {/* Gradient accent glow on hover */}
        <div 
          className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
          style={{
            background: `radial-gradient(ellipse at top, ${area.gradientFrom}, ${area.gradientTo}, transparent 70%)`
          }}
        />
        
        {/* Shimmer Effect */}
        <div className="absolute inset-0 glass-shimmer opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
        
        <CardContent className="p-5 sm:p-6 lg:p-8 relative z-10 flex-1 flex flex-col">
          {/* Header: Icon + Title */}
          <div className="flex items-center gap-3 sm:gap-4 mb-5 sm:mb-6">
            <div className={`w-14 h-14 sm:w-16 sm:h-16 rounded-xl bg-gradient-to-br ${area.accentColor} flex items-center justify-center shadow-liquid-md group-hover:scale-110 group-hover:rotate-3 transition-all duration-500`}>
              <Icon className="w-7 h-7 sm:w-8 sm:h-8 text-white drop-shadow-lg" />
            </div>
            <div>
              <h3 className="text-xl sm:text-2xl font-montserrat font-bold text-white group-hover:text-white transition-colors">
                {area.title}
              </h3>
              <div className={`h-0.5 w-12 mt-1.5 rounded-full bg-gradient-to-r ${area.accentColor} opacity-80`} />
            </div>
          </div>
          
          {/* Services List */}
          <ul className="space-y-2.5 sm:space-y-3 flex-1">
            {area.services.map((service, idx) => (
              <li 
                key={idx} 
                className="text-sm sm:text-base text-white/80 font-inter leading-relaxed flex items-center group/item hover:text-white transition-colors duration-300"
              >
                <span 
                  className={`w-2 h-2 rounded-full bg-gradient-to-r ${area.accentColor} mr-3 flex-shrink-0 group-hover/item:scale-125 transition-transform duration-300`}
                />
                {service}
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>
    </AnimatedSection>
  );
};

const ServiceAreas = () => {
  return (
    <section className="py-20 md:py-28 relative overflow-hidden bg-gradient-to-b from-dark-section via-industrial-gray/95 to-dark-section">
      {/* Animated Background Blobs */}
      <div className="absolute top-20 left-10 w-72 h-72 md:w-96 md:h-96 bg-primary/20 rounded-full blur-3xl animate-liquid-blob opacity-30" />
      <div className="absolute bottom-20 right-10 w-64 h-64 md:w-80 md:h-80 bg-accent/15 rounded-full blur-3xl animate-liquid-blob-reverse opacity-25" />
      
      {/* Dotted Pattern Overlay */}
      <div 
        className="absolute inset-0 opacity-5"
        style={{
          backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.3) 1px, transparent 1px)',
          backgroundSize: '24px 24px'
        }}
      />
      
      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <AnimatedSection animation="fade-in" className="text-center mb-12 md:mb-16">
          {/* Badge */}
          <div className="inline-flex items-center px-3 py-1.5 sm:px-4 sm:py-2 rounded-full glass-liquid-light border border-white/20 mb-4 sm:mb-6">
            <span className="text-xs sm:text-sm font-montserrat font-semibold text-white/90 tracking-wider">
              SECTORES QUE ATENDEMOS
            </span>
          </div>
          
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-montserrat font-bold mb-4 sm:mb-6 text-white text-balance">
            ÁREAS DE SERVICIO
          </h2>
          <p className="text-base sm:text-lg text-white/70 max-w-2xl mx-auto font-inter leading-relaxed px-4">
            Soluciones eléctricas especializadas para cada sector
          </p>
        </AnimatedSection>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
          {serviceAreas.map((area, index) => (
            <ServiceAreaCard key={index} area={area} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServiceAreas;
